import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface InvoiceRow {
  id: string;
  number: string | null;
  status: string | null;          // 'paid' | 'open' | 'void' | 'uncollectible' | 'draft' | null
  amount_due_cents: number;
  amount_paid_cents: number;
  currency: string;
  created_at: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  period_start: string | null;
  period_end: string | null;
}

interface InvoiceListResponse {
  invoices?: InvoiceRow[];
  has_more?: boolean;
  error?: string;
}

/**
 * Fetches the signed-in patient's recent Stripe invoices via the
 * `patient-list-invoices` edge function. The function reads Stripe directly,
 * so the list always reflects the source of truth (no DB shadowing).
 *
 * Returns an empty list when the patient has no stripe_customer_id linked
 * — the Billing page renders its own "no activity" empty state in that case.
 */
export function useInvoices(opts?: { limit?: number; enabled?: boolean }) {
  const limit = opts?.limit ?? 20;
  return useQuery<InvoiceRow[]>({
    queryKey: ["invoices", limit],
    enabled: opts?.enabled !== false,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<InvoiceListResponse>(
        "patient-list-invoices",
        { body: { limit } },
      );
      if (error) throw new Error(error.message ?? "Failed to load invoices");
      if (data?.error) throw new Error(data.error);
      return data?.invoices ?? [];
    },
    staleTime: 60_000, // 1 minute — invoices change rarely.
  });
}
